import React, { useState } from 'react';
import { Plus, Edit, Trash2, Eye, Search, Filter, Upload } from 'lucide-react';
import { useContacts, useDeleteContact, Contact } from '@/hooks/useContacts';
import { useBrands } from '@/hooks/useBrands';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useFilteredContacts } from '@/hooks/useFilteredContacts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import ContactForm from './contacts/ContactForm';
import ContactViewModal from './contacts/ContactViewModal';
import ContactsFilter from './contacts/ContactsFilter';
import { ImportContactsModal } from './contacts/ImportContactsModal';

const ContactsScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { data: contacts = [], isLoading, refetch } = useContacts();
  const { data: brands = [] } = useBrands();
  const { data: campaigns = [] } = useCampaigns();
  const deleteContact = useDeleteContact();

  const {
    filters,
    setFilters,
    filteredContacts,
    clearFilters,
    hasActiveFilters,
  } = useFilteredContacts(contacts);

  const searchFilteredContacts = filteredContacts.filter(contact =>
    (contact.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (contact.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (contact.company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getBrandNames = (brandIds: string[] = []) => {
    return brands
      .filter(brand => brandIds.includes(brand.id))
      .map(brand => brand.name)
      .join(', ');
  };

  const getCampaignNames = (campaignIds: string[] = []) => {
    return campaigns
      .filter(campaign => campaignIds.includes(campaign.id))
      .map(campaign => campaign.name)
      .join(', ');
  };

  const handleEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setIsEditModalOpen(true);
  };

  const handleView = (contact: Contact) => {
    setSelectedContact(contact);
    setIsViewModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    await deleteContact.mutateAsync(id);
  };

  const handleImportSuccess = () => {
    refetch();
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64">Loading contacts...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
          <p className="text-gray-600">Manage stakeholders and contacts for brands and campaigns</p>
        </div>
        <div className="flex space-x-3">
          <Button 
            variant="outline" 
            onClick={() => setIsImportModalOpen(true)}
            className="border-purple-200 text-purple-700 hover:bg-purple-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Contact</DialogTitle>
              </DialogHeader>
              <ContactForm onSuccess={() => setIsAddModalOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search contacts by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button 
          variant="outline" 
          onClick={() => setIsFilterOpen(true)}
          className={hasActiveFilters ? "bg-blue-50 border-blue-200 text-blue-700" : ""}
        >
          <Filter className="w-4 h-4 mr-2" />
          Filter
          {hasActiveFilters && (
            <Badge variant="secondary" className="ml-2 px-1 py-0 text-xs bg-blue-100 text-blue-700">
              Active
            </Badge>
          )}
        </Button>
      </div>

      {/* Active Filters Indicator */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
          <span className="text-sm text-blue-700">
            Filters are active • Showing {searchFilteredContacts.length} of {contacts.length} contacts
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters}
            className="text-blue-700 hover:bg-blue-100"
          >
            Clear all filters
          </Button>
        </div>
      )}

      {/* Contacts Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Brands</TableHead>
              <TableHead>Campaigns</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {searchFilteredContacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell>
                  <div>
                    <div className="font-medium text-gray-900">{contact.name}</div>
                    {contact.company_name && (
                      <div className="text-sm text-gray-500">{contact.company_name}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800">
                    {contact.email}
                  </a>
                </TableCell>
                <TableCell>
                  {contact.phone_number ? (
                    <a href={`tel:${contact.phone_number}`} className="text-blue-600 hover:text-blue-800">
                      {contact.phone_number}
                    </a>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {contact.designation || <span className="text-gray-400">-</span>}
                </TableCell>
                <TableCell>
                  {contact.contact_type ? (
                    <Badge variant="secondary">{contact.contact_type}</Badge>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="max-w-xs truncate" title={getBrandNames(contact.linked_brand_ids)}>
                    {getBrandNames(contact.linked_brand_ids) || <span className="text-gray-400">-</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="max-w-xs truncate" title={getCampaignNames(contact.linked_campaign_ids)}>
                    {getCampaignNames(contact.linked_campaign_ids) || <span className="text-gray-400">-</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleView(contact)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(contact)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete {contact.name}? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(contact.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {searchFilteredContacts.length === 0 && (
          <div className="text-center py-8">
            <div className="text-gray-500">No contacts found</div>
            {(searchTerm || hasActiveFilters) && (
              <div className="text-sm text-gray-400 mt-1">
                Try adjusting your search terms or filters
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filter Component */}
      <ContactsFilter
        filters={filters}
        onFiltersChange={setFilters}
        onClearFilters={clearFilters}
        isOpen={isFilterOpen}
        onOpenChange={setIsFilterOpen}
      />

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <ContactForm
              contact={selectedContact}
              onSuccess={() => setIsEditModalOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <ContactViewModal
        contact={selectedContact}
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
      />

      {/* Import Modal */}
      <ImportContactsModal
        open={isImportModalOpen}
        onOpenChange={setIsImportModalOpen}
        onImported={handleImportSuccess}
      />
    </div>
  );
};

export default ContactsScreen;
